import {
  AlertOutlined,
  ApartmentOutlined,
  ApiOutlined,
  AuditOutlined,
  BankOutlined,
  BarChartOutlined,
  BookOutlined,
  CloudServerOutlined,
  ClusterOutlined,
  ControlOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  DeploymentUnitOutlined,
  DesktopOutlined,
  FileSearchOutlined,
  FileTextOutlined,
  HeartOutlined,
  IdcardOutlined,
  InfoCircleOutlined,
  KeyOutlined,
  MonitorOutlined,
  NotificationOutlined,
  RocketOutlined,
  SafetyCertificateOutlined,
  SaveOutlined,
  SettingOutlined,
  SwapOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  UserOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons';
import { platformPermissions } from '@/services/business/permissions';
import type { AppMenuItem, AppRouteMeta } from './types';

export const defaultRoute: AppRouteMeta = {
  id: '/dashboard',
  label: '运行总览',
  labelEn: 'Runtime overview',
  path: '/dashboard',
  pageKind: 'business',
  closable: false,
  permission: platformPermissions.dashboardRead,
};

function placeholderItem(
  path: string,
  name: string,
  nameEn: string,
  icon: AppMenuItem['icon'],
  permission: AppMenuItem['permission'],
): AppMenuItem {
  return { key: path, path, pageKind: 'placeholder', name, nameEn, icon, permission };
}

// 两级导航：只包含设计文档中的“必选”二级页面；推荐/可选页面暂不显示。
// 业务页面尚未实现的二级页面使用 'placeholder' 占位，仅用于导航骨架。
export const templateNavigationItems: AppMenuItem[] = [
  {
    key: 'overview',
    name: '总览',
    nameEn: 'Overview',
    icon: <DashboardOutlined aria-hidden />,
    children: [
      {
        key: defaultRoute.id,
        name: defaultRoute.label,
        nameEn: defaultRoute.labelEn,
        icon: <DashboardOutlined aria-hidden />,
        path: defaultRoute.path,
        pageKind: defaultRoute.pageKind,
        permission: defaultRoute.permission,
      },
      placeholderItem(
        '/overview/usage',
        '用量明细',
        'Usage details',
        <BarChartOutlined aria-hidden />,
        platformPermissions.usageRead,
      ),
    ],
  },
  {
    key: 'cluster',
    name: '集群与节点',
    nameEn: 'Clusters and nodes',
    icon: <ClusterOutlined aria-hidden />,
    children: [
      placeholderItem(
        '/cluster/clusters',
        '集群管理',
        'Cluster management',
        <ClusterOutlined aria-hidden />,
        platformPermissions.clustersRead,
      ),
      {
        key: '/cluster/servers',
        path: '/cluster/servers',
        pageKind: 'business',
        name: '服务器管理',
        nameEn: 'Server management',
        icon: <CloudServerOutlined aria-hidden />,
        permission: platformPermissions.serversRead,
      },
      {
        key: '/cluster/instances',
        path: '/cluster/instances',
        pageKind: 'business',
        name: '网关实例',
        nameEn: 'Gateway instances',
        icon: <DeploymentUnitOutlined aria-hidden />,
        permission: platformPermissions.instancesRead,
      },
      placeholderItem(
        '/cluster/rollouts',
        '配置发布',
        'Config rollouts',
        <RocketOutlined aria-hidden />,
        platformPermissions.rolloutsRead,
      ),
    ],
  },
  {
    key: 'routing',
    name: '渠道与路由',
    nameEn: 'Channels and routing',
    icon: <SwapOutlined aria-hidden />,
    children: [
      placeholderItem(
        '/routing/channels',
        '服务渠道',
        'Service channels',
        <ApiOutlined aria-hidden />,
        platformPermissions.channelsRead,
      ),
      {
        key: '/routing/accounts',
        path: '/routing/accounts',
        pageKind: 'business',
        name: '上游账号',
        nameEn: 'Upstream accounts',
        icon: <UserOutlined aria-hidden />,
        permission: platformPermissions.accountsRead,
      },
      placeholderItem(
        '/routing/models',
        '模型目录',
        'Model catalog',
        <DatabaseOutlined aria-hidden />,
        platformPermissions.modelsRead,
      ),
      placeholderItem(
        '/routing/policies',
        '路由策略',
        'Routing policies',
        <ApartmentOutlined aria-hidden />,
        platformPermissions.routingRead,
      ),
      placeholderItem(
        '/routing/quotas',
        '限流与配额',
        'Rate limits and quotas',
        <ThunderboltOutlined aria-hidden />,
        platformPermissions.quotasRead,
      ),
    ],
  },
  {
    key: 'access',
    name: '访问管理',
    nameEn: 'Access management',
    icon: <SafetyCertificateOutlined aria-hidden />,
    children: [
      {
        key: '/access/api-keys',
        path: '/access/api-keys',
        pageKind: 'business',
        name: 'API Keys',
        nameEn: 'API keys',
        icon: <KeyOutlined aria-hidden />,
        permission: platformPermissions.apiKeysRead,
      },
      {
        // 复用母版用户管理页（含角色分配）；角色管理页保留为隐藏路由。
        key: '/organization/users',
        path: '/organization/users',
        pageKind: 'business',
        name: '成员与角色',
        nameEn: 'Members and roles',
        icon: <TeamOutlined aria-hidden />,
        permission: platformPermissions.usersRead,
      },
    ],
  },
  {
    key: 'monitoring',
    name: '监控与日志',
    nameEn: 'Monitoring and logs',
    icon: <MonitorOutlined aria-hidden />,
    children: [
      placeholderItem(
        '/monitoring/request-logs',
        '请求日志',
        'Request logs',
        <FileSearchOutlined aria-hidden />,
        platformPermissions.requestLogsRead,
      ),
      placeholderItem(
        '/monitoring/health',
        '健康状态',
        'Health status',
        <HeartOutlined aria-hidden />,
        platformPermissions.healthRead,
      ),
      {
        key: '/operations/audit-logs',
        path: '/operations/audit-logs',
        pageKind: 'business',
        name: '操作审计',
        nameEn: 'Audit logs',
        icon: <AuditOutlined aria-hidden />,
        permission: platformPermissions.logsRead,
      },
    ],
  },
  {
    key: 'system-settings',
    name: '系统设置',
    nameEn: 'System settings',
    icon: <SettingOutlined aria-hidden />,
    children: [
      {
        key: '/system/settings',
        path: '/system/settings',
        pageKind: 'business',
        name: '基础设置',
        nameEn: 'Basic settings',
        icon: <ControlOutlined aria-hidden />,
        permission: platformPermissions.settingsRead,
      },
      placeholderItem(
        '/system/backup',
        '备份与恢复',
        'Backup and restore',
        <SaveOutlined aria-hidden />,
        platformPermissions.backupRead,
      ),
      {
        key: '/system/help/errors',
        path: '/system/help/errors',
        pageKind: 'business',
        name: '帮助与文档',
        nameEn: 'Help and documentation',
        icon: <BookOutlined aria-hidden />,
        permission: platformPermissions.aboutRead,
      },
    ],
  },
];

function hiddenItem(
  path: string,
  name: string,
  nameEn: string,
  icon: AppMenuItem['icon'],
  permission: AppMenuItem['permission'],
): AppMenuItem {
  return { key: path, path, pageKind: 'business', name, nameEn, icon, permission };
}

// 母版遗留的示例页面：代码与路由保留（可直接访问、可打开标签），但不在网关两级导航中显示。
export const hiddenPageItems: AppMenuItem[] = [
  hiddenItem(
    '/access/roles',
    '角色管理',
    'Roles',
    <UserSwitchOutlined aria-hidden />,
    platformPermissions.rolesRead,
  ),
  hiddenItem(
    '/organization/departments',
    '部门管理',
    'Departments',
    <BankOutlined aria-hidden />,
    platformPermissions.departmentsRead,
  ),
  hiddenItem(
    '/organization/positions',
    '岗位管理',
    'Positions',
    <IdcardOutlined aria-hidden />,
    platformPermissions.positionsRead,
  ),
  hiddenItem(
    '/system/dictionaries',
    '字典管理',
    'Dictionaries',
    <BookOutlined aria-hidden />,
    platformPermissions.dictionariesRead,
  ),
  hiddenItem(
    '/system/announcements',
    '公告管理',
    'Announcements',
    <NotificationOutlined aria-hidden />,
    platformPermissions.announcementsRead,
  ),
  hiddenItem(
    '/operations/login-logs',
    '登录日志',
    'Login logs',
    <FileTextOutlined aria-hidden />,
    platformPermissions.logsRead,
  ),
  hiddenItem(
    '/security/events',
    '安全事件',
    'Security events',
    <AlertOutlined aria-hidden />,
    platformPermissions.settingsRead,
  ),
  hiddenItem(
    '/security/sessions',
    '在线会话',
    'Sessions',
    <DesktopOutlined aria-hidden />,
    platformPermissions.settingsRead,
  ),
  hiddenItem(
    '/security/policies',
    '访问策略',
    'Access policies',
    <ControlOutlined aria-hidden />,
    platformPermissions.settingsRead,
  ),
  hiddenItem(
    '/system/about',
    '关于系统',
    'About',
    <InfoCircleOutlined aria-hidden />,
    platformPermissions.aboutRead,
  ),
];

export const templatePageItems = [...templateNavigationItems, ...hiddenPageItems];
